import React, { Dispatch, FC, ReactNode, SetStateAction, useContext, useState } from 'react';
import { ModalBody, ModalContent, ModalFooter, ModalHeader, ModalLoader } from '@fiverr-private/overlay';
import { Box } from '@fiverr-private/theme';
import { Button, IconButton } from '@fiverr-private/button';
import { TimesIcon } from '@fiverr-private/visuals';
import { Divider, Stack } from '@fiverr-private/layout_components';
import { Heading, Text } from '@fiverr-private/typography';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { logger } from '@fiverr-private/obs';
import { LEARN_MORE_BACKGROUND, LEARN_MORE_CLASSIFICATION } from '../../constants';
import { reportEventsApi } from '../../biEvents';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ComplianceContext } from '../ComplianceProvider';
import TextWithLink from './TextWithLink';
import { ClassificationForm, ComplianceType, ComplianceTypes, COPIES_PREFIX } from './types';
import ButtonWithArrowLeft from './ButtonWIthArrowLeft';
import { getComplianceImage, getCSSParams, getFooter } from './helpers';

interface ComplianceModalContentProps {
    infoGrid: ReactNode;
    isLoading: boolean;
    refetchPackagesData: () => void;
    refetchSellerConfigs: () => void;
    packageTypeTitle: string;
    checkedComplianceGrid: JSX.Element | null;
    discountInPercents: number;
    currency: string;
    complianceType: ComplianceType;
    requeseteeId: number;
    requeseteeUserName: string;
    classificationForm: ClassificationForm | undefined;
    step: number;
    setStep: Dispatch<SetStateAction<number>>;
    isOpen: boolean;
    onClose: () => void;
}
const prepareWFCquestionsSubmitData = (classificationData: ClassificationForm) => {
    const questionKeys = Object.keys(classificationData);
    return questionKeys.reduce(
        (acc, key, index) => ({
            ...acc,
            [`wc_q${index + 1}`]: {
                answer: classificationData[key],
                question: translate(`compliance.questioner.questions.${key}`, {
                    locale: 'en-US',
                }),
            },
        }),
        {}
    );
};

export const ComplianceModalContent: FC<ComplianceModalContentProps> = ({
    infoGrid,
    isLoading,
    refetchPackagesData,
    refetchSellerConfigs,
    packageTypeTitle,
    checkedComplianceGrid,
    discountInPercents,
    currency,
    complianceType,
    requeseteeId,
    requeseteeUserName,
    classificationForm,
    step,
    setStep,
    isOpen,
    onClose,
}) => {
    const complianceContext = useContext(ComplianceContext);

    const header = `${COPIES_PREFIX}.${complianceType}.header`;
    const body = `${COPIES_PREFIX}.${complianceType}.body`;

    const image = getComplianceImage(complianceType);
    const { gap } = getCSSParams(complianceType);
    const footer = getFooter(complianceType, 'special_terms');

    const hasBack = step > 0;
    const isMobile = useIsMobile();

    const backStep = () => {
        reportEventsApi.clickedOnClassificationModalBack({
            pageName: complianceContext?.pageName ?? '',
            sellerId: requeseteeId,
            discountInPercents,
        });

        setStep((step) => (hasBack ? step - 1 : step));
    };

    const isFree = discountInPercents === 100;

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handlePayment = async () => {
        try {
            setIsSubmitting(true);
            const completedClassification =
                classificationForm && complianceType === ComplianceTypes.WORKFORCE_CLASSIFICATION;
            let classificationAnswers;
            if (completedClassification) {
                classificationAnswers = prepareWFCquestionsSubmitData(classificationForm);
            }

            const eventData = {
                pageName: complianceContext?.pageName ?? '',
                sellerId: requeseteeId,
                complianceType,
                discountInPercents,
            };

            reportEventsApi.onPaymentClick(eventData);

            const response = await complianceContext?.api?.handlePayment({
                complianceType,
                currency,
                requesteeId: requeseteeId,
                classificationAnswers,
                requeseteeUserName,
                discountInPercents,
            });

            if (response?.isRedirecting) {
                return;
            }

            if (response?.shouldRefetchData) {
                refetchPackagesData();
                refetchSellerConfigs();
            }

            setIsSubmitting(false);

            onClose();
        } catch (error) {
            logger.error(error as Error);
            setIsSubmitting(false);
        }
    };

    const handleAnimationEnd = () => {
        if (isOpen) {
            reportEventsApi.onModalView({
                pageName: complianceContext?.pageName ?? '',
                sellerId: requeseteeId,
                discountInPercents,
                complianceType,
            });
        }
    };

    return (
        <ModalContent onAnimationEnd={handleAnimationEnd}>
            <ModalHeader>
                <Box height="8px" />
                <Box position="absolute" top="14px" right="14px">
                    <IconButton aria-label="X" variant="ghost" size={'3xs'} onClick={onClose}>
                        <TimesIcon />
                    </IconButton>
                </Box>
            </ModalHeader>
            <ModalLoader isLoading={isLoading}>
                <ModalBody>
                    <Stack direction={'column'} gap={gap} marginBottom={'4'}>
                        {image}
                        <Stack direction={'column'} gap={'2'} color={'grey_1100'}>
                            <Heading as="h4" size={isMobile ? 'h_sm' : 'h_md'} color={'inherit'}>
                                <I18n k={header} />
                            </Heading>
                            <TextWithLink
                                color={'bodyPrimary'}
                                body={body}
                                link={
                                    complianceType === ComplianceTypes.BACKGROUND_CHECK
                                        ? LEARN_MORE_BACKGROUND
                                        : LEARN_MORE_CLASSIFICATION
                                }
                                size={'b_md'}
                            />
                        </Stack>
                        <Box
                            borderColor={'grey_500'}
                            borderWidth={'sm'}
                            borderStyle={'solid'}
                            borderRadius={'lg'}
                            direction={'column'}
                        >
                            <Stack gap={'4'} direction={'column'} padding={'5'}>
                                <Heading as="h6">
                                    <I18n k={packageTypeTitle} />
                                </Heading>
                                <Box style={{ margin: '0 -20px' }}>
                                    <Divider direction="horizontal" />
                                </Box>
                                {checkedComplianceGrid}
                                {infoGrid}
                            </Stack>
                        </Box>
                    </Stack>
                    {footer}
                </ModalBody>
            </ModalLoader>
            <Divider direction="horizontal" />
            <ModalFooter>
                <Stack width={'100%'} direction={'row'} justifyContent={'spaceBetween'}>
                    {hasBack && <ButtonWithArrowLeft onBack={backStep} />}
                    <Box justifySelf={'flexEnd'} width={'100%'} display={'flex'} justifyContent={'flexEnd'} gap={'3'}>
                        <Button onClick={onClose} variant={'ghost'}>
                            <Text size={'b_md'} fontWeight={'semibold'}>
                                {translate(`${COPIES_PREFIX}.cancel`)}
                            </Text>
                        </Button>
                        <Button onClick={handlePayment} disabled={isSubmitting} loading={isSubmitting}>
                            <Text size={'b_sm'} color={'white'} fontWeight={'semibold'}>
                                {translate(`${COPIES_PREFIX}.${isFree ? 'send_request' : 'continue_to_payment'}`)}
                            </Text>
                        </Button>
                    </Box>
                </Stack>
            </ModalFooter>
        </ModalContent>
    );
};
